import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import Icon from '../docs/Icon'
import Dialog from '../ui/Dialog'
import AgencyMark from '../marks/AgencyMark'
import MissionPicker from '../missions/MissionPicker'
import ThemeToggle from './ThemeToggle'
import Lockup from './Lockup'
import { america, findMission } from '@/content/missions'
import { usePrefs, type Prefs } from '@/state/prefs'
import Editable, { type EditOption } from '../inspector/Editable'
import { primaryLinks } from '@/content/navigation'
import { brandOptions } from '../home/options'
import { useBrand } from '@/state/useBrand'
import { useCasing } from '@/state/useCasing'
import ColorsNote from '../inspector/ColorsNote'
import { useScrolled } from '@/state/useScrolled'
import HomeLink from './HomeLink'
import { useIsHome } from '@/state/useIsHome'

const wordmarkOptions: Array<EditOption<Prefs['wordmark']>> = [
  { value: 'sans', label: 'Sans' },
  { value: 'sans-bold', label: 'Sans bold' },
  { value: 'serif', label: 'Serif' },
  { value: 'mono', label: 'Mono caps' },
]

const navOptions: Array<EditOption<Prefs['nav']>> = [
  { value: 'menu', label: 'Menu button' },
  { value: 'hamburger', label: 'Hamburger' },
  { value: 'links', label: 'Links' },
]

const layoutOptions: Array<EditOption<string>> = [
  { value: 'full', label: 'Full name' },
  { value: 'stacked', label: 'Stacked' },
  { value: 'initials', label: 'Initials' },
]

function HeaderBar() {
  const { prefs, set } = usePrefs()
  const isHome = useIsHome()
  const scrolled = useScrolled()
  const [menuOpen, setMenuOpen] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const mission = findMission(prefs.mission) ?? america
  const { name } = useBrand()
  const cased = useCasing()
  const close = () => setMenuOpen(false)
  const notes = isHome && !scrolled

  return (
    <header className="mds-header">
      <div className="mds-container mds-header__inner">
        <Editable
          label="Wordmark"
          value={prefs.wordmark}
          options={wordmarkOptions}
          onChange={(value) => set('wordmark', value)}
          optionsLabel="Font"
          more={[
            {
              label: 'Layout',
              value: prefs.wordmarkLayout,
              options: layoutOptions,
              onChange: (value) => set('wordmarkLayout', value as Prefs['wordmarkLayout']),
            },
            {
              label: 'Name',
              value: prefs.brand,
              options: brandOptions,
              onChange: (value) => set('brand', value as Prefs['brand']),
            },
          ]}
          token="data-wordmark"
          pin={prefs.nav === 'links' ? 'below' : 'right'}
          className="mds-header__brand"
          enabled={notes}
        >
          <HomeLink className="mds-header__home" aria-label={`${name} home`}>
            <Lockup />
          </HomeLink>
        </Editable>
        <Editable
          label="Navigation"
          value={prefs.nav}
          options={navOptions}
          onChange={(value) => set('nav', value)}
          token="data-nav"
          pin="below"
          className="mds-header__nav-wrap"
          enabled={notes && prefs.nav === 'links'}
        >
          <nav aria-label="Main" className="mds-header__nav">
            {primaryLinks.map((link) => (
              <NavLink key={link.to} to={link.to} className="mds-header__link">
                {cased(link.label)}
              </NavLink>
            ))}
          </nav>
        </Editable>
        <div className="mds-header__actions">
          <ColorsNote enabled={notes}>
            <button
              type="button"
              className="mds-mission-chip"
              onClick={() => setPickerOpen(true)}
              aria-haspopup="dialog"
            >
              <span className="mds-mission-chip__swatch" aria-hidden="true" />
              <span className="mds-mission-chip__label">
                <span className="mds-visually-hidden">Mission: </span>
                {mission.id === america.id ? cased('Choose mission') : mission.short}
              </span>
            </button>
          </ColorsNote>
          <ThemeToggle />
          <Editable
            label="Navigation"
            value={prefs.nav}
            options={navOptions}
            onChange={(value) => set('nav', value)}
            token="data-nav"
            pin="below-center"
            className="mds-header__menu-wrap"
            enabled={notes}
          >
            <button
              type="button"
              className={
                prefs.nav === 'menu'
                  ? 'mds-button mds-button--primary mds-button--sm mds-header__menu'
                  : 'mds-icon-button mds-header__menu mds-header__menu--icon'
              }
              aria-haspopup="dialog"
              aria-label={prefs.nav === 'menu' ? undefined : 'Menu'}
              onClick={() => setMenuOpen(true)}
            >
              {prefs.nav === 'menu' ? cased('Menu') : <Icon name="menu" />}
            </button>
          </Editable>
        </div>
      </div>
      <Dialog open={menuOpen} onClose={close} title="Menu" variant="sheet">
        <nav aria-label="Menu" className="mds-menu">
          <ul className="mds-menu__primary">
            {primaryLinks.map((link) => (
              <li key={link.to}>
                <NavLink to={link.to} onClick={close}>
                  {cased(link.label)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <button
          type="button"
          className="mds-button mds-button--secondary"
          onClick={() => {
            close()
            setPickerOpen(true)
          }}
        >
          <AgencyMark mission={mission} variant={prefs.mark} className="mds-menu__mark" />
          {mission.id === america.id ? cased('Choose your mission') : `${cased('Mission')}: ${mission.name}`}
        </button>
      </Dialog>
      <MissionPicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
    </header>
  )
}

export default function SiteHeader() {
  const { pathname } = useLocation()
  return <HeaderBar key={pathname} />
}
